import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Text,
  TextInput,
  Stack,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconEdit,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { styleTableMantine } from "../../../_base/_const/_constVar";
import BreadCrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import {
  deleteCategoryTradeInSetting,
  getListCategoryTradeIn,
} from "../../../api/ApiTradeIn";
import { _localization } from "../../../config/location";
import { FormCategoryModal } from "./FormCategoryModal";
import { RulesViewModal } from "./RulesViewModal";

// TS Interfaces
export interface RuleCriterion {
  id: string | number;
  text: string;
  isActive: boolean;
  percent?: number | null;
  amount?: number | null;
}

export interface TradeCategoryHeader {
  id: number;
  option: number; // 1 = buyback, 2 = upgrade
  productCondition: string;
  percentSupport: number;
  amountSupport: number | null;
  shortDescription: string;
  includedAccessories: string;
  missingAccessoryNote: string;
  tblTradeInLines: RuleCriterion[];
  mergedIds?: number[];
}

export interface TradeCategoryRule {
  id: string | number;
  name: string; // compatibility name
  groupName: string;
  linkIndex: string;
  type: string; // "THU MUA" | "LÊN ĐỜI"
  categoryType: "buyback" | "upgrade"; // compatibility
  status: string; // "1" | "0"
  orderNumber: number;
  quantity?: number;
  percentSupport?: number | null;
  maxAmountSupport?: number | null;
  description?: string | null;
  youtubeLink?: string | null;
  
  // Dynamic headers list
  headers: TradeCategoryHeader[];

  // Extended attributes
  attribute1?: string | null;
}

// Map Backend Response Item to Frontend State
export const mapCategoryResponseToState = (item: any): TradeCategoryRule => {
  const listHeaders = item.listTblTradeInHeader || [];
  
  const headerMap: Record<string, TradeCategoryHeader> = {};
  const orderedKeys: string[] = [];

  for (const h of listHeaders) {
    const productCondition = h.productCondition || "";
    const option = h.option || (productCondition === "LÊN ĐỜI" || productCondition === "LEN DOI" ? 2 : 1);
    const key = `${option}-${productCondition.trim().toUpperCase()}`;

    const lines = h.listTblTradeInLine || h.tblTradeInLines || h.tblTradeInLineModels || [];
    const mappedLines = lines.map((l: any) => ({
      id: l.id,
      text: l.description || "",
      isActive: true,
      percent: l.percent,
      amount: l.amount
    }));

    if (headerMap[key]) {
      const existing = headerMap[key];
      
      // Collect duplicate header IDs
      if (h.id && h.id !== 0 && h.id !== "0") {
        const hIdNum = Number(h.id);
        const existingIdNum = Number(existing.id);
        
        if (!existingIdNum) {
          existing.id = hIdNum;
        } else if (existingIdNum !== hIdNum) {
          existing.mergedIds = existing.mergedIds || [];
          if (!existing.mergedIds.includes(hIdNum)) {
            existing.mergedIds.push(hIdNum);
          }
        }
      }
      
      // Preserve other fields if empty
      if (!existing.shortDescription && h.shortDescription) {
        existing.shortDescription = h.shortDescription;
      }
      if (!existing.includedAccessories && h.includedAccessories) {
        existing.includedAccessories = h.includedAccessories;
      }
      if (!existing.missingAccessoryNote && h.missingAccessoryNote) {
        existing.missingAccessoryNote = h.missingAccessoryNote;
      }
      
      // Merge criteria lines by keeping unique IDs or descriptions
      const lineMap = new Map<string, RuleCriterion>();
      existing.tblTradeInLines.forEach(l => {
        const k = l.id ? `id-${l.id}` : `text-${l.text}`;
        lineMap.set(k, l);
      });
      mappedLines.forEach((l: any) => {
        const k = l.id ? `id-${l.id}` : `text-${l.text}`;
        if (!lineMap.has(k)) {
          lineMap.set(k, l);
        }
      });
      existing.tblTradeInLines = Array.from(lineMap.values());
    } else {
      headerMap[key] = {
        id: Number(h.id) || 0,
        option,
        productCondition,
        percentSupport: h.percentSupport || 0,
        amountSupport: h.amountSupport || null,
        shortDescription: h.shortDescription || "",
        includedAccessories: h.includedAccessories || "",
        missingAccessoryNote: h.missingAccessoryNote || "",
        tblTradeInLines: mappedLines,
        mergedIds: []
      };
      orderedKeys.push(key);
    }
  }

  const mappedHeaders = orderedKeys.map(key => headerMap[key]);

  // Sắp xếp các header: option 1 (thu mua) lên đầu và xếp theo số của productCondition (Loại 1, Loại 2...), option 2 (lên đời) ở cuối
  mappedHeaders.sort((a, b) => {
    if (a.option !== b.option) {
      return a.option - b.option;
    }
    const numA = parseInt(a.productCondition.replace(/\D/g, ""), 10) || 0;
    const numB = parseInt(b.productCondition.replace(/\D/g, ""), 10) || 0;
    if (numA !== numB) {
      return numA - numB;
    }
    return a.productCondition.localeCompare(b.productCondition);
  });

  return {
    id: item.id,
    name: item.groupName || item.title || "",
    groupName: item.groupName || item.title || "",
    linkIndex: item.linkIndex || item.url || "",
    type: item.type || "THU MUA",
    categoryType: item.type === "THU MUA" ? "buyback" : "upgrade",
    status:
      item.status === true ||
      item.status === 1 ||
      item.status === "1" ||
      item.status === "active"
        ? "1"
        : "0",
    orderNumber: item.orderNumber || 1,
    quantity: item.quantity || 0,
    percentSupport: item.percentSupport,
    maxAmountSupport: item.maxAmountSupport,
    description: item.description || item.content || "",
    youtubeLink: item.youtubeLink || item.linkYoutube || item.youtubeEmbedded || "",
    headers: mappedHeaders,
    attribute1: item.attribute1 || null
  };
};

export const TradeInCategoryV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const tableHeight = isMobile ? undefined : "calc(100vh - 300px)";

  // Rules View Modal states
  const [isRulesViewModalOpen, setIsRulesViewModalOpen] = useState(false);
  const [selectedRulesCategory, setSelectedRulesCategory] = useState<TradeCategoryRule | null>(null);

  // Form Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCategoryIdForEdit, setSelectedCategoryIdForEdit] = useState<string | number | null>(null);



  // Master UI State for category list
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [categories, setCategories] = useState<TradeCategoryRule[]>([]);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchVal, setSearchVal] = useState("");

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});

  // Load Categories from Backend API
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await getListCategoryTradeIn({
        keySearch: searchQuery,
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
      });
      if (response && response.success) {
        let items: any[] = [];
        let totalCount = 0;
        
        if (response.data) {
          if (Array.isArray(response.data)) {
            items = response.data;
            totalCount = response.totalCount || response.data.length;
          } else if (response.data.lists) {
            items = response.data.lists;
            totalCount = response.data.count || 0;
          }
        } else if (Array.isArray(response.lists)) {
          items = response.lists;
          totalCount = response.count || response.lists.length;
        }

        const mappedList = items.map((item: any) => mapCategoryResponseToState(item));
        mappedList.sort((a, b) => {
          const numA = parseInt(a.attribute1 || "", 10);
          const numB = parseInt(b.attribute1 || "", 10);
          const hasNumA = !isNaN(numA);
          const hasNumB = !isNaN(numB);
          
          if (hasNumA && hasNumB) {
            return numA - numB;
          }
          if (hasNumA) return -1;
          if (hasNumB) return 1;
          if (a.attribute1 && b.attribute1) {
            return a.attribute1.localeCompare(b.attribute1);
          }
          return 0;
        });
        setCategories(mappedList);
        setTotal(totalCount);
      } else {
        setCategories([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      NotificationExtension.Fails("Không thể tải danh sách ngành hàng!");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchVal);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  useEffect(() => {
    fetchCategories();
  }, [pagination.pageIndex, pagination.pageSize, searchQuery]);

  // Listen for navigation state redirects (e.g. from TradeInProduct)
  useEffect(() => {
    if (location.state?.categoryId && categories.length > 0) {
      const cat = categories.find((c) => String(c.id) === String(location.state.categoryId));
      if (cat) {
        handleStartEdit(cat.id);
      }
    }
  }, [location.state, categories]);

  const handleStartAdd = () => {
    setSelectedCategoryIdForEdit(null);
    setIsFormOpen(true);
  };

  const handleStartEdit = (catId: string | number) => {
    setSelectedCategoryIdForEdit(catId);
    setIsFormOpen(true);
  };

  // Columns for Category Table
  const columns = useMemo<MRT_ColumnDef<TradeCategoryRule>[]>(
    () => [
      {
        id: "index",
        header: "STT",
        size: 70,
        Cell: ({ row }) => row.original.attribute1 || pagination.pageIndex * pagination.pageSize + row.index + 1,
      },
      {
        accessorKey: "linkIndex",
        header: "Mã ngành hàng",
        size: 150,
        Cell: ({ cell }) => (
          <span style={{ fontWeight: 600, fontFamily: "monospace", color: "var(--mantine-color-gray-7)" }}>
            {cell.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "groupName",
        header: "Ngành hàng",
        size: 180,
        mantineTableBodyCellProps: {
          style: { fontWeight: 600 }
        }
      },
      {
        accessorKey: "type",
        header: "Loại ngành hàng",
        size: 150,
        Cell: ({ cell }) => {
          const val = cell.getValue<string>();
          return val === "THU MUA" ? (
            <Badge color="orange" variant="light">THU MUA</Badge>
          ) : (
            <Badge color="blue" variant="light">LÊN ĐỜI</Badge>
          );
        },
      },
      {
        id: "buybackRules",
        header: "Quy tắc thu mua",
        size: 180,
        Cell: ({ row }) => {
          const cat = row.original;
          const isUpgrade = cat.type === "LÊN ĐỜI" || cat.type === "LEN DOI" || cat.categoryType === "upgrade";
          if (isUpgrade) return <span style={{ color: "var(--mantine-color-gray-4)", fontStyle: "italic", fontSize: "12px" }}>N/A</span>;
          
          const buybacks = cat.headers.filter(h => h.option === 1);
          if (buybacks.length === 0) return <span style={{ color: "var(--mantine-color-gray-4)", fontStyle: "italic", fontSize: "12px" }}>N/A</span>;
          
          const percents = buybacks.map(h => h.percentSupport).filter(p => typeof p === "number") as number[];
          let rangeText = "";
          if (percents.length > 0) {
            const minVal = Math.min(...percents);
            const maxVal = Math.max(...percents);
            rangeText = minVal === maxVal ? `${minVal}%` : `${minVal}% - ${maxVal}%`;
          }
          
          return (
            <Button
              variant="light"
              color="blue"
              size="xs"
              radius="xl"
              style={{
                height: 26,
                padding: "0 12px",
                fontSize: "12px",
                fontWeight: 600,
              }}
              onClick={() => {
                setSelectedRulesCategory(cat);
                setIsRulesViewModalOpen(true);
              }}
              title="Click để xem chi tiết quy tắc"
            >
              {buybacks.length} mức phân loại {rangeText ? `(${rangeText})` : ""}
            </Button>
          );
        },
      },
      {
        id: "supportPercentage",
        header: "Hỗ trợ lên đời",
        size: 140,
        Cell: ({ row }) => {
          const cat = row.original;
          const isUpgrade = cat.type === "LÊN ĐỜI" || cat.type === "LEN DOI" || cat.categoryType === "upgrade";
          if (!isUpgrade) return <span style={{ color: "var(--mantine-color-gray-4)", fontStyle: "italic", fontSize: "12px" }}>N/A</span>;
          
          const upHead = cat.headers.find(h => h.option === 2);
          const pct = cat.percentSupport !== undefined && cat.percentSupport !== null
            ? cat.percentSupport
            : (upHead ? upHead.percentSupport : 0);
          return (
            <span style={{ fontWeight: 600, color: "var(--mantine-color-gray-8)" }}>
              {pct}%
            </span>
          );
        },
      },
      {
        id: "maxSupportAmount",
        header: "Giới hạn hỗ trợ",
        size: 150,
        Cell: ({ row }) => {
          const cat = row.original;
          
          if (cat.maxAmountSupport !== undefined && cat.maxAmountSupport !== null) {
            return (
              <span style={{ fontWeight: 600, color: "var(--mantine-color-gray-8)" }}>
                {cat.maxAmountSupport.toLocaleString()}đ
              </span>
            );
          }

          const isUpgrade = cat.type === "LÊN ĐỜI" || cat.type === "LEN DOI" || cat.categoryType === "upgrade";
          if (!isUpgrade) return <span style={{ color: "var(--mantine-color-gray-4)", fontStyle: "italic", fontSize: "12px" }}>N/A</span>;

          const upHead = cat.headers.find(h => h.option === 2);
          const amt = upHead ? upHead.amountSupport || 0 : 0;
          return (
            <span style={{ fontWeight: 600, color: "var(--mantine-color-gray-8)" }}>
              {amt.toLocaleString()}đ
            </span>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        size: 120,
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          return (
            <Badge color={status === "1" ? "green" : "gray"} variant="light">
              {status === "1" ? "Đang áp dụng" : "Tạm dừng"}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "Thao tác",
        size: 90,
        Cell: ({ row }) => {
          const cat = row.original;
          return (
            <Group gap={4} justify="center">
              <ActionIcon
                variant="light"
                color="orange"
                title="Sửa thông tin"
                onClick={() => handleStartEdit(cat.id)}
              >
                <IconEdit size={16} />
              </ActionIcon>
              <ActionIcon
                variant="light"
                color="red"
                title="Xóa ngành hàng"
                onClick={() => handleDeleteSingleCategory(cat.id, cat.groupName)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
          );
        },
        enableSorting: false,
        enableColumnActions: false,
      },
    ],
    [navigate, categories, pagination]
  );

  const table = useMantineReactTable({
    columns,
    data: categories,
    enableColumnResizing: false,
    enableSorting: true,
    enableColumnActions: false,
    enableTopToolbar: false,
    positionToolbarAlertBanner: "none",
    enablePagination: true,
    manualPagination: true,
    rowCount: total,
    enableRowSelection: true,
    getRowId: (row) => String(row.id),
    onRowSelectionChange: setRowSelection,
    initialState: {
      columnOrder: [
        "mrt-row-select",
        "index",
        "linkIndex",
        "groupName",
        "type",
        "buybackRules",
        "supportPercentage",
        "maxSupportAmount",
        "status",
        "actions"
      ]
    },
    state: {
      pagination,
      isLoading: loading,
      rowSelection,
    },
    onPaginationChange: setPagination,
    localization: _localization,
    mantineTableContainerProps: {
      style: {
        height: tableHeight,
      },
    },
    mantineTableBodyCellProps: () => ({
      style: {
        ...styleTableMantine,
        padding: "12px 10px",
      },
    }),
    mantineTableHeadCellProps: () => ({
      style: {
        padding: "12px 10px",
      },
    }),
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "md",
      size: "md",
      rowsPerPageOptions: ["5", "10", "20", "50"],
    },
    paginationDisplayMode: "pages",
  });

  const handleDeleteSingleCategory = (id: number | string, name: string) => {
    modals.openConfirmModal({
      title: "Xóa ngành hàng",
      children: (
        <Text size="sm">
          Bạn có chắc chắn muốn xóa ngành hàng <strong>{name}</strong> khỏi hệ thống? 
          Hành động này không thể hoàn tác.
        </Text>
      ),
      labels: { confirm: "Đồng ý", cancel: "Hủy" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const res = await deleteCategoryTradeInSetting([Number(id)]);
          if (res && res.success) {
            await fetchCategories();
          }
        } catch (error) {
          console.error("Error deleting category:", error);
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleDeleteSelectedCategories = () => {
    const selectedIds = Object.keys(rowSelection).map(Number);
    if (selectedIds.length === 0) return;

    modals.openConfirmModal({
      title: "Xóa nhiều ngành hàng",
      children: (
        <Text size="sm">
          Bạn có chắc chắn muốn xóa {selectedIds.length} ngành hàng đã chọn? 
          Hành động này không thể hoàn tác.
        </Text>
      ),
      labels: { confirm: "Đồng ý", cancel: "Hủy" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const res = await deleteCategoryTradeInSetting(selectedIds);
          if (res && res.success) {
            setRowSelection({});
            await fetchCategories();
          }
        } catch (error) {
          console.error("Error deleting categories:", error);
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  return (
    <Box p="md" style={{ width: "100%", boxSizing: "border-box", position: "relative" }}>
      <LoadingOverlay visible={actionLoading} zIndex={1000} />
      <Group gap={0} justify="space-between" mb={10}>
        <BreadCrumb />
      </Group>

      {/* Category List */}
      <Paper p="md" withBorder radius="md">
        <Group justify="space-between" mb="md" wrap="nowrap">
           {/* Filter toolbar */}
        <Group gap="sm" mb="md">
          <TextInput
            placeholder="Tìm kiếm ngành hàng..."
            leftSection={<IconSearch size={16} />}
            value={searchVal}
            onChange={(e) => setSearchVal(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
            size="sm"
            style={{ width: 220 }}
          />
          <Button
            leftSection={<IconSearch size={16} />}
            onClick={handleSearch}
            size="sm"
            color="blue"
          >
            Tìm kiếm
          </Button>
        </Group>
          <Group gap="xs">
            {Object.keys(rowSelection).length > 0 && (
              <Button
                color="red"
                variant="filled"
                size="sm"
                leftSection={<IconTrash size={16} />}
                onClick={handleDeleteSelectedCategories}
              >
                Xóa ({Object.keys(rowSelection).length})
              </Button>
            )}
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleStartAdd}
              size="sm"
            >
              Thêm ngành hàng
            </Button>
          </Group>
        </Group>

        {/* Table */}
        <MantineReactTable table={table} />
      </Paper>



      {/* Modal: Rules Viewer */}
      <RulesViewModal
        opened={isRulesViewModalOpen}
        onClose={() => {
          setIsRulesViewModalOpen(false);
          setSelectedRulesCategory(null);
        }}
        category={selectedRulesCategory}
      />

      {/* Modal: Add/Edit Category */}
      <FormCategoryModal
        opened={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setSelectedCategoryIdForEdit(null);
        }}
        categoryId={selectedCategoryIdForEdit}
        categories={categories}
        onSave={() => {
          setIsFormOpen(false);
          setSelectedCategoryIdForEdit(null);
          fetchCategories();
        }}
      />
    </Box>
  );
};

export default TradeInCategoryV2;
